import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';
import theme from './theme/index.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCProvider } from './trpc/client';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import type { RentalsPaymentsPortalRouter } from '@src/backend/src/rentals/functions/paymentsPortal';
import CustomFallback from './components/CustomFallback.tsx';
import RouterContainer from './containers/RouterContainer.tsx';
import { InvoicesToPayProvider } from './contexts/InvoicesToPayContext.tsx';
import { InvoiceDataProvider } from './contexts/InvoiceDataContext.tsx';
import { AuthContextProvider } from './contexts/AuthContext.tsx';
import { MerchantContextProvider } from './contexts/MerchantContext.tsx';
import { BreakpointsProvider } from './contexts/BreakpointsContext';
import { SubscriptionsProvider } from './contexts/SubscriptionsContext.tsx';
import { StepperProvider } from './contexts/StepperContext.tsx';
import { TabProvider } from './contexts/TabContext.tsx';
import { ChallengeModalProvider } from './contexts/ChallengeModalContext.tsx';
import { PaymentModalStatusProvider } from './contexts/PaymentModalStatusContext.tsx';
import { capturePaymentSource } from './helpers/paymentSource.ts';
import * as Sentry from '@sentry/react';

capturePaymentSource();

Sentry.init({
  dsn: 'https://6a6525a70eed83c8503cd2b3d207221a@o4509516442042368.ingest.us.sentry.io/4509561623347200',
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  allowUrls: ['pagos.palomma.com'],
});

const queryClient = new QueryClient();
const trpcClient = createTRPCClient<RentalsPaymentsPortalRouter>({
  links: [httpBatchLink({ url: import.meta.env.VITE_TRPC_API_URL })],
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <Sentry.ErrorBoundary fallback={<CustomFallback />}>
        <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <BreakpointsProvider>
              <MerchantContextProvider>
                <AuthContextProvider>
                  <InvoiceDataProvider>
                    <SubscriptionsProvider>
                      <InvoicesToPayProvider>
                        <StepperProvider>
                          <TabProvider>
                            <PaymentModalStatusProvider>
                              <ChallengeModalProvider>
                                <RouterContainer />
                              </ChallengeModalProvider>
                            </PaymentModalStatusProvider>
                          </TabProvider>
                        </StepperProvider>
                      </InvoicesToPayProvider>
                    </SubscriptionsProvider>
                  </InvoiceDataProvider>
                </AuthContextProvider>
              </MerchantContextProvider>
            </BreakpointsProvider>
          </QueryClientProvider>
        </TRPCProvider>
      </Sentry.ErrorBoundary>
    </ChakraProvider>
  </React.StrictMode>
);
